import { useState } from 'react';

import { Typography } from '../Typography';

import { TimePicker as TimePickerAntd } from 'antd';
import { Dayjs } from 'dayjs';
import { HiMiniXCircle, HiOutlineClock } from 'react-icons/hi2';

import { ETypographyIntents } from '@src/typings';
import { timePickerVariants } from '@src/utilities/timePickerVariants';
import { StyledFormItem } from '../FileUpload/styles';
import { useTheme, withTheme } from '../ThemeProvider';
import { StyledTimePickerOverride } from './styles';
import { DayjsValue, TimePickerProps } from './types';

const TimePickerComponent = ({
    value,
    defaultValue,
    placeholder,
    name,
    placement = 'bottomLeft',
    allowClear = true,
    disabled = false,
    format = 'HH:mm:ss',
    disabledTime,
    needConfirm = true,
    error,
    layoutLabel = 'vertical',
    size = 'middle',
    renderFooter,
    disabledDate,
    className,
    style,
    label,
    onChange,
    formItemProps,
    inputMobileProps,
    range,
    intent = 'outlined',
    ...props
}: Readonly<TimePickerProps>) => {
    const { required, ...formItemLabelProps } = formItemProps ?? {};
    const { theme } = useTheme();

    const [tempSelection, setTempSelection] = useState<DayjsValue>(
        value || defaultValue || null
    );

    const handleChange = (time: DayjsValue) => {
        setTempSelection(time);
        onChange?.(time);
    };
    const handleOpenChange = (open: boolean) => {
        document.body.style.overflow = open ? 'hidden' : 'auto';
    };

    const getTimePickerClassName = () => {
        const baseClasses = timePickerVariants({
            intent,
            size,
            error: !!error,
        });
        return className ? `${baseClasses} ${className}` : baseClasses;
    };

    const SuffixIcon = () => {
        return (
            <>
                {tempSelection ? (
                    <button className={'rounded-md bg-transparent'}>
                        <HiMiniXCircle
                            className="w-5 h-5 bg-transparent"
                            style={{ color: theme.colors.secondary.light.text }}
                        />
                    </button>
                ) : (
                    <button
                        className={`rounded-md ${size === 'small' ? 'p-1' : 'p-2'}`}
                        style={{
                            backgroundColor: theme.colors.primary.light.filled,
                        }}
                    >
                        <HiOutlineClock
                            style={{
                                color: theme.colors.primary.main.filled,
                            }}
                        />
                    </button>
                )}
            </>
        );
    };

    const FormItemLabel = () => {
        if (!label) return;
        return (
            <Typography
                intent={ETypographyIntents.Regular}
                className="flex items-center justify-start gap-x-1"
            >
                <span>{label}</span>sd
                {required && !formItemProps?.required && (
                    <span
                        style={{ color: theme.colors.error.main.filled }}
                        className="text-lg"
                    >
                        *
                    </span>
                )}
            </Typography>
        );
    };

    const RangeTimePicker = (
        <StyledFormItem
            name={name}
            layout={layoutLabel}
            label={<FormItemLabel />}
            required={formItemProps?.required ? true : false}
            {...formItemLabelProps}
        >
            <StyledTimePickerOverride />
            <TimePickerAntd.RangePicker
                allowClear={allowClear}
                format={format}
                size={size}
                disabled={disabled}
                disabledTime={disabledTime}
                className={getTimePickerClassName()}
                getPopupContainer={() => document.body}
                placeholder={placeholder as [string, string]}
                suffixIcon={<SuffixIcon />}
                inputReadOnly
                style={style}
                needConfirm={needConfirm}
                onOpenChange={handleOpenChange}
                disabledDate={disabledDate}
                defaultValue={tempSelection as [Dayjs, Dayjs]}
                value={tempSelection as [Dayjs, Dayjs]}
                onChange={(dates: any) => {
                    if (dates) {
                        handleChange(dates);
                    } else {
                        handleChange(null);
                    }
                }}
            />

            <Typography
                intent={ETypographyIntents.Badge}
                className="mt-1"
                style={{ color: theme.colors.error.main.filled }}
            >
                {error}
            </Typography>
        </StyledFormItem>
    );

    const SingleTimePicker = (
        <StyledFormItem
            name={name}
            layout={layoutLabel}
            label={label}
            {...formItemProps}
        >
            <StyledTimePickerOverride />
            <TimePickerAntd
                {...props}
                disabled={disabled}
                allowClear={allowClear}
                placement={placement}
                placeholder={placeholder as string}
                size={size}
                format={format}
                disabledTime={disabledTime}
                className={getTimePickerClassName()}
                inputReadOnly
                style={style}
                needConfirm={needConfirm}
                showNow={false}
                onOpenChange={handleOpenChange}
                disabledDate={disabledDate}
                defaultValue={defaultValue as Dayjs}
                value={tempSelection as Dayjs}
                onChange={(time) => handleChange(time)}
                suffixIcon={<SuffixIcon />}
                required={formItemProps?.required ? true : false}
            />
            <Typography
                intent={ETypographyIntents.Badge}
                className="mt-1"
                style={{ color: theme.colors.error.main.filled }}
            >
                {error}
            </Typography>
        </StyledFormItem>
    );

    return <>{range ? RangeTimePicker : SingleTimePicker}</>;
};

export const TimePicker = withTheme(TimePickerComponent);
